import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter,
  Package,
  AlertTriangle,
  ChevronDown,
  Edit,
  Save,
  X
} from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { StatusBadge } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { rawMaterialService } from '../../services/rawMaterialService';
import { RawMaterial, RawMaterialFilters, MATERIAL_TYPE_OPTIONS, UNIT_OPTIONS } from '../../types/rawMaterial';

/**
 * Formatação do NCM conforme padrão XXXX.XX.XX
 */
const formatNCM = (value: string): string => {
  const clean = (value || '').replace(/\D/g, '');
  if (clean.length <= 4) return clean;
  if (clean.length <= 6) return `${clean.slice(0,4)}.${clean.slice(4)}`;
  return `${clean.slice(0,4)}.${clean.slice(4,6)}.${clean.slice(6,8)}`;
};

/**
 * Página de listagem de matérias-primas
 * Inclui filtros, busca, paginação e ações baseadas em permissões
 */
export const RawMaterialListView: React.FC = () => {
  const navigate = useNavigate();
  
  const [materials, setMaterials] = useState<(RawMaterial & Record<string, any>)[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  
  // Estados dos filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    is_active: true,
    low_stock: false,
    out_of_stock: false,
    in_stock: false,
    materialType: undefined as number | undefined,
  });
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;
  
  // Estatísticas rápidas
  const [totalMaterials, setTotalMaterials] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [outOfStockCount, setOutOfStockCount] = useState(0);

  // Carrega os materiais
  const loadMaterials = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const searchFilters: RawMaterialFilters = {
        page: currentPage,
        pageSize: pageSize,
        searchName: search.trim() || undefined,
        is_active: filters.is_active,
        low_stock: filters.low_stock,
        out_of_stock: filters.out_of_stock,
        in_stock: filters.in_stock,
        materialType: filters.materialType,
      };
      
      const response = await rawMaterialService.getRawMaterials(searchFilters);
      
      const rawData = response.data;
      const materialsArray: RawMaterial[] = Array.isArray(rawData) ? rawData : [];
      const paginationData = response.pagination;
      
      setMaterials(materialsArray.map(m => ({
        ...m,
        isEditingDescription: false,
        isEditingNcm: false,
        isEditingCost: false,
        isEditingStatus: false,
        tempDescription: m.description,
        tempNcm: formatNCM(m.ncm || ''),
        tempCost: m.cost !== undefined && m.cost !== null ? String(m.cost) : '',
        tempActive: m.is_active,
      })));
      
      setTotalMaterials(paginationData?.total || materialsArray.length);
      setTotalPages(paginationData?.totalPages || 1);
      
      // Calcular estatísticas
      const lowStock = materialsArray.filter(m => 
        (m.stock_quantity || 0) > 0 && (m.stock_quantity || 0) <= (m.min_stock_quantity || 5)
      ).length;
      const outOfStock = materialsArray.filter(m => 
        (m.stock_quantity || 0) === 0
      ).length;
      
      setLowStockCount(lowStock);
      setOutOfStockCount(outOfStock);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar matérias-primas');
    } finally {
      setLoading(false);
    }
  };

  // Carregamento inicial
  useEffect(() => {
    loadMaterials();
  }, []);

  // Carrega quando página ou filtros mudam
  useEffect(() => {
    loadMaterials();
  }, [currentPage, filters]);

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadMaterials();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [search]);

  // Fechar dropdown de filtros ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown') && !target.closest('[data-filter-trigger]')) {
        setShowFilters(false);
      }
    };

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters]);

  // Badge de status do estoque
  const getStockStatus = (material: RawMaterial) => {
    const stockQty = material.stock_quantity || 0;
    const minStock = material.min_stock_quantity || 5;
    
    if (stockQty === 0) {
      return <StatusBadge status="sem-estoque" />;
    }
    if (stockQty <= minStock) {
      return <StatusBadge status="estoque-baixo" />;
    }
    return <StatusBadge status="em-estoque" />;
  };

  // Label do tipo de material
  const getMaterialTypeLabel = (type: number) => {
    const option = MATERIAL_TYPE_OPTIONS.find(o => o.value === type);
    return option ? option.label : 'Desconhecido';
  };

  // Label da unidade
  const getUnitLabel = (unit?: string) => {
    if (!unit) return '-';
    const option = UNIT_OPTIONS.find(o => o.value === unit);
    return option ? option.value : unit;
  };

  // Helpers para edição inline
  const startEditDescription = (id: number) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, isEditingDescription: true, tempDescription: m.description } : m));
  };

  const cancelEditDescription = (id: number) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, isEditingDescription: false, tempDescription: m.description, hasError: false } : m));
  };

  const saveDescription = async (id: number) => {
    const material = materials.find(m => m.id === id);
    if (!material) return;
    const newDesc = (material.tempDescription || '').trim();
    if (!newDesc) {
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, hasError: true, errorMessage: 'Descrição não pode ficar vazia' } : m));
      return;
    }

    try {
      await rawMaterialService.updateRawMaterial(id, { description: newDesc });
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, description: newDesc, isEditingDescription: false, hasError: false } : m));
    } catch (err) {
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, hasError: true, errorMessage: err instanceof Error ? err.message : 'Erro ao salvar descrição' } : m));
    }
  };

  const startEditNcm = (id: number) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, isEditingNcm: true, tempNcm: formatNCM(m.ncm || '') } : m));
  };

  const cancelEditNcm = (id: number) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, isEditingNcm: false, tempNcm: formatNCM(m.ncm || ''), hasError: false } : m));
  };

  const saveNcm = async (id: number) => {
    const material = materials.find(m => m.id === id);
    if (!material) return;
    const newNcm = (material.tempNcm || '').trim();

    try {
      await rawMaterialService.updateRawMaterial(id, { ncm: newNcm || undefined });
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, ncm: newNcm || undefined, isEditingNcm: false, hasError: false } : m));
    } catch (err) {
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, hasError: true, errorMessage: err instanceof Error ? err.message : 'Erro ao salvar NCM' } : m));
    }
  };

  const startEditCost = (id: number) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, isEditingCost: true, tempCost: m.cost !== undefined && m.cost !== null ? String(m.cost) : '' } : m));
  };

  const cancelEditCost = (id: number) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, isEditingCost: false, tempCost: m.cost !== undefined && m.cost !== null ? String(m.cost) : '', hasError: false } : m));
  };

  const saveCost = async (id: number) => {
    const material = materials.find(m => m.id === id);
    if (!material) return;
    const parsed = parseFloat((material.tempCost || '').replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, hasError: true, errorMessage: 'Custo inválido' } : m));
      return;
    }

    try {
      await rawMaterialService.updateRawMaterial(id, { cost: parsed });
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, cost: parsed, isEditingCost: false, hasError: false } : m));
    } catch (err) {
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, hasError: true, errorMessage: err instanceof Error ? err.message : 'Erro ao salvar custo' } : m));
    }
  };

  const startEditStatus = (id: number) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, isEditingStatus: true, tempActive: !!m.is_active } : m));
  };

  const cancelEditStatus = (id: number) => {
    setMaterials(prev => prev.map(m => m.id === id ? { ...m, isEditingStatus: false, tempActive: m.is_active } : m));
  };

  const saveStatus = async (id: number) => {
    const material = materials.find(m => m.id === id);
    if (!material) return;
    try {
      await rawMaterialService.updateRawMaterial(id, { is_active: !!material.tempActive });
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, is_active: !!material.tempActive, isEditingStatus: false, hasError: false } : m));
    } catch (err) {
      setMaterials(prev => prev.map(m => m.id === id ? { ...m, hasError: true, errorMessage: err instanceof Error ? err.message : 'Erro ao salvar status' } : m));
    }
  };

  const handleKeyPressField = (e: React.KeyboardEvent, id: number, saveFn: (id: number) => Promise<void> | void, cancelFn: (id: number) => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveFn(id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelFn(id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-h1 mb-2">Matérias-Primas</h1>
          <p className="text-slate-600">
            Gerenciar matérias-primas e insumos do estoque
          </p>
        </div>
        <PermissionGuard permission="raw_materials.create">
          <Button
            icon={<Plus size={20} />}
            onClick={() => navigate('/raw-materials/new')}
          >
            Nova Matéria-Prima
          </Button>
        </PermissionGuard>
      </div>

      {/* Estatísticas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {totalMaterials.toLocaleString('pt-BR')}
                </p>
                <p className="text-small text-slate-500">Total de materiais</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{lowStockCount}</p>
                <p className="text-small text-slate-500">Estoque baixo</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{outOfStockCount}</p>
                <p className="text-small text-slate-500">Sem estoque</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Package className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {totalMaterials - lowStockCount - outOfStockCount}
                </p>
                <p className="text-small text-slate-500">Disponível</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e busca */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Input
                  placeholder="Buscar por código, descrição ou NCM..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  startIcon={<Search size={20} />}
                />
                {search && loading && (
                  <div className="absolute right-3 top-2.5">
                    <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                )}
              </div>
              {search && (
                <p className="text-xs text-slate-500 mt-1">
                  Buscando por: <span className="font-medium">"{search}"</span>
                  {totalMaterials >= 0 && !loading && (
                    <span> - {totalMaterials} resultado{totalMaterials !== 1 ? 's' : ''}</span>
                  )}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Button 
                  variant={
                    Object.entries(filters).filter(([key, value]) => key !== 'is_active' && key !== 'materialType' && value === true).length > 0 
                    || !filters.is_active 
                    || filters.materialType !== undefined 
                      ? "primary" : "outline"
                  } 
                  size="sm" 
                  icon={<Filter size={16} />}
                  onClick={() => setShowFilters(!showFilters)}
                  data-filter-trigger
                >
                  Filtros
                  {(Object.entries(filters).filter(([key, value]) => key !== 'is_active' && key !== 'materialType' && value === true).length > 0 
                    || !filters.is_active
                    || filters.materialType !== undefined) ? (
                    <span className="ml-1 bg-white text-primary rounded-full px-1.5 py-0.5 text-xs font-medium">
                      {Object.entries(filters).filter(([key, value]) => key !== 'is_active' && key !== 'materialType' && value === true).length 
                        + (!filters.is_active ? 1 : 0) 
                        + (filters.materialType !== undefined ? 1 : 0)}
                    </span>
                  ) : null}
                  <ChevronDown size={14} className={`ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </Button>
                
                {showFilters && (
                  <div className="filter-dropdown absolute right-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <div className="p-4">
                      <h3 className="font-medium text-gray-900 mb-3">Filtrar materiais</h3>
                      
                      <div className="space-y-3">
                        {/* Status */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Status</h4>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.is_active}
                                onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.checked }))}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm">Materiais ativos</span>
                            </label>
                          </div>
                        </div>

                        {/* Tipo de Material */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Tipo de Material</h4>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="materialType"
                                checked={filters.materialType === undefined}
                                onChange={() => setFilters(prev => ({ ...prev, materialType: undefined }))}
                                className="border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm">Todos</span>
                            </label>
                            {MATERIAL_TYPE_OPTIONS.map(option => (
                              <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="materialType"
                                  checked={filters.materialType === option.value}
                                  onChange={() => setFilters(prev => ({ ...prev, materialType: option.value }))}
                                  className="border-gray-300 text-primary focus:ring-primary"
                                />
                                <span className="text-sm">{option.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        {/* Estoque */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Estoque</h4>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.in_stock}
                                onChange={(e) => setFilters(prev => ({ ...prev, in_stock: e.target.checked }))}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm">Em estoque</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.low_stock}
                                onChange={(e) => setFilters(prev => ({ ...prev, low_stock: e.target.checked }))}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm">Estoque baixo</span>
                            </label>
                            
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.out_of_stock}
                                onChange={(e) => setFilters(prev => ({ ...prev, out_of_stock: e.target.checked }))}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm">Sem estoque</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-4 pt-3 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFilters({
                              is_active: true,
                              low_stock: false,
                              out_of_stock: false,
                              in_stock: false,
                              materialType: undefined,
                            });
                          }}
                          className="flex-1"
                        >
                          Limpar
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setShowFilters(false)}
                          className="flex-1"
                        >
                          Aplicar
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de materiais */}
      <Card>
        <CardHeader title="Lista de Matérias-Primas" />
        {search && (
          <div className="px-6 pb-2 text-sm text-slate-600">
            Resultados da busca para: "<span className="font-medium">{search}</span>"
            {totalMaterials > 0 && (
              <span className="text-slate-500">
                {" - "}
                {totalMaterials} material{totalMaterials !== 1 ? 'is' : ''} encontrado{totalMaterials !== 1 ? 's' : ''}
                {totalPages > 1 && (
                  <span> (página {currentPage} de {totalPages})</span>
                )}
              </span>
            )}
          </div>
        )}
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando matérias-primas...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={loadMaterials} variant="outline" size="sm">
                Tentar novamente
              </Button>
            </div>
          ) : materials.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                {search ? 'Nenhuma matéria-prima encontrada' : 'Nenhuma matéria-prima cadastrada'}
              </p>
              <PermissionGuard permission="raw_materials.create">
                <Button onClick={() => navigate('/raw-materials/new')}>
                  Cadastrar primeira matéria-prima
                </Button>
              </PermissionGuard>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Código</th>
                  <th className="text-left">Descrição</th>
                  <th className="text-center">Tipo</th>
                  <th className="text-center">Unidade</th>
                  <th className="text-left">NCM</th>
                  <th className="text-right">Custo</th>
                  <th className="text-center">Estoque</th>
                  <th className="text-center">Status Estoque</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {materials.map((material) => (
                  <TableRow key={material.id}>
                    <TableCell>
                      <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                        {material.code}
                      </code>
                    </TableCell>
                    <TableCell>
                      {material.isEditingDescription ? (
                        <div className="flex items-center gap-2">
                          <input
                            className="border rounded px-2 py-1 w-full"
                            value={material.tempDescription}
                            onChange={(e) => setMaterials(prev => prev.map(m => m.id === material.id ? { ...m, tempDescription: e.target.value, hasError: false } : m))}
                            onKeyDown={(e) => handleKeyPressField(e, material.id, saveDescription, cancelEditDescription)}
                          />
                          <button onClick={() => saveDescription(material.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded">
                            <Save size={14} />
                          </button>
                          <button onClick={() => cancelEditDescription(material.id)} className="p-2 text-slate-500 hover:bg-slate-50 rounded">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{material.description}</p>
                          </div>
                          <button onClick={() => startEditDescription(material.id)} className="p-1 text-slate-500 hover:text-slate-700">
                            <Edit size={14} />
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        material.material_type === 0 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-cyan-100 text-cyan-800'
                      }`}>
                        {getMaterialTypeLabel(material.material_type)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm text-slate-700">{getUnitLabel(material.unit)}</span>
                    </TableCell>
                    <TableCell>
                      {material.isEditingNcm ? (
                        <div className="flex items-center gap-2">
                          <input
                            className="border rounded px-2 py-1 w-36"
                            value={material.tempNcm}
                            onChange={(e) => setMaterials(prev => prev.map(m => m.id === material.id ? { ...m, tempNcm: formatNCM(e.target.value), hasError: false } : m))}
                            onKeyDown={(e) => handleKeyPressField(e, material.id, saveNcm, cancelEditNcm)}
                          />
                          <button onClick={() => saveNcm(material.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded">
                            <Save size={14} />
                          </button>
                          <button onClick={() => cancelEditNcm(material.id)} className="p-2 text-slate-500 hover:bg-slate-50 rounded">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            {material.ncm ? (
                              <span className="text-sm text-slate-700">{material.ncm}</span>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </div>
                          <button onClick={() => startEditNcm(material.id)} className="p-1 text-slate-500 hover:text-slate-700">
                            <Edit size={14} />
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {material.isEditingCost ? (
                        <div className="flex items-center justify-end gap-2">
                          <input
                            className="border rounded px-2 py-1 w-28 text-right"
                            value={material.tempCost}
                            onChange={(e) => setMaterials(prev => prev.map(m => m.id === material.id ? { ...m, tempCost: e.target.value, hasError: false } : m))}
                            onKeyDown={(e) => handleKeyPressField(e, material.id, saveCost, cancelEditCost)}
                          />
                          <button onClick={() => saveCost(material.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded">
                            <Save size={14} />
                          </button>
                          <button onClick={() => cancelEditCost(material.id)} className="p-2 text-slate-500 hover:bg-slate-50 rounded">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end">
                          <p className="font-medium">
                            {typeof material.cost === 'number' ? material.cost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '-'}
                          </p>
                          <button onClick={() => startEditCost(material.id)} className="ml-2 p-1 text-slate-500 hover:text-slate-700">
                            <Edit size={14} />
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div>
                        <p className="font-medium">{material.stock_quantity}</p>
                        {material.min_stock_quantity !== undefined && (
                          <p className="text-xs text-slate-500">
                            Min: {material.min_stock_quantity}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {getStockStatus(material)}
                    </TableCell>
                    <TableCell className="text-center">
                      {material.isEditingStatus ? (
                        <div className="flex items-center justify-center gap-2">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" checked={!!material.tempActive} onChange={(e) => setMaterials(prev => prev.map(m => m.id === material.id ? { ...m, tempActive: e.target.checked } : m))} />
                            <span className="text-sm">Ativo</span>
                          </label>
                          <button onClick={() => saveStatus(material.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded">
                            <Save size={14} />
                          </button>
                          <button onClick={() => cancelEditStatus(material.id)} className="p-2 text-slate-500 hover:bg-slate-50 rounded">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            material.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {material.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                          <button onClick={() => startEditStatus(material.id)} className="p-1 text-slate-500 hover:text-slate-700">
                            <Edit size={14} />
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/raw-materials/${material.id}`)}
                          className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-slate-700 bg-transparent border border-slate-300 hover:bg-slate-50 focus:ring-emerald-500 px-3 py-1.5 text-sm"
                          aria-label={`Visualizar material ${material.code}`}
                        >
                          <span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye">
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </span>
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
          
          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <div className="text-sm text-gray-500">
                Mostrando {Math.min((currentPage - 1) * pageSize + 1, totalMaterials)} a {Math.min(currentPage * pageSize, totalMaterials)} de {totalMaterials} materiais
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || loading}
                >
                  Anterior
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "primary" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        disabled={loading}
                        className="w-8 h-8 p-0"
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages || loading}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
